import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import {
  CalendarIcon,
  UserGroupIcon,
  BanknotesIcon,
  MapPinIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  PencilIcon,
  TrophyIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { getTournamentEvents, supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Tournament } from './TournamentList';
import type { Event } from '../types/content';

interface TournamentDetailsProps {
  tournament: Tournament;
  onBack: () => void;
  onEdit: (tournament: Tournament) => void;
}

export default function TournamentDetails({ 
  tournament, 
  onBack,
  onEdit
}: TournamentDetailsProps) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseName, setCourseName] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    fetchEvents();
    fetchCourse();
  }, [tournament.id]);

  const fetchCourse = async () => {
    const { data } = await supabase
      .from('tournament_courses')
      .select('courses(name, city, country)')
      .eq('tournament_id', tournament.id)
      .eq('is_primary', true)
      .maybeSingle();
    if (data?.courses) {
      const c = data.courses as { name: string; city: string | null; country: string | null };
      const parts = [c.name, c.city, c.country].filter(Boolean);
      setCourseName(parts.join(', '));
    } else {
      setCourseName(null);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await getTournamentEvents(tournament.id);
      
      if (error) throw error;
      
      if (data) {
        const formattedEvents = data.map(event => ({
          id: event.id,
          title: event.title,
          time: event.time,
          date: event.date,
          location: event.location,
          description: event.description || ''
        }));
        
        setEvents(formattedEvents);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'ongoing':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Group events by date
  const eventsByDate = useMemo(() => {
    return events.reduce((acc, event) => {
      const date = event.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(event);
      return acc;
    }, {} as Record<string, Event[]>);
  }, [events]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-golf-orange hover:text-golf-green transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Back to Tournaments</span>
        </button>
        <button
          onClick={() => onEdit(tournament)}
          className="flex items-center gap-2 text-golf-orange hover:text-golf-green transition-colors"
        >
          <PencilIcon className="w-5 h-5" />
          <span>Edit Tournament</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => setIsExpanded(prev => !prev)}
          className="w-full p-6 text-left focus:outline-none"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <h3 className="text-xl font-bold text-golf-gray">{tournament.name}</h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(tournament.status)}`}>
                  {tournament.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  <span>
                    {format(new Date(tournament.start_date), 'MMMM d')} - {format(new Date(tournament.end_date), 'MMMM d, yyyy')}
                  </span>
                </div>
                {tournament.venue && (
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4" />
                    <span>{tournament.venue}</span>
                  </div>
                )}
                {courseName && (
                  <div className="flex items-center gap-2">
                    <TrophyIcon className="w-4 h-4" />
                    <span>{courseName}</span>
                  </div>
                )}
              </div>
            </div>
            <ChevronDownIcon
              className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <div className="border-t border-gray-200" />
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <UserGroupIcon className="w-5 h-5 text-blue-600" />
                      <h3 className="font-medium text-golf-gray">Capacity</h3>
                    </div>
                    <p className="text-2xl font-bold text-golf-gray">{tournament.capacity} <span className="text-sm font-normal text-gray-500">Players</span></p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <BanknotesIcon className="w-5 h-5 text-green-600" />
                      <h3 className="font-medium text-golf-gray">Registration Fee</h3>
                    </div>
                    <p className="text-2xl font-bold text-golf-gray">{formatCurrency(tournament.registration_fee)}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <BanknotesIcon className="w-5 h-5 text-amber-600" />
                      <h3 className="font-medium text-golf-gray">Prize Pool</h3>
                    </div>
                    <p className="text-2xl font-bold text-golf-gray">{formatCurrency(tournament.prize_pool)}</p>
                  </div>
                </div>

                {tournament.description && (
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <DocumentTextIcon className="w-5 h-5 text-golf-orange" />
                      <h3 className="text-lg font-medium text-golf-gray">Description</h3>
                    </div>
                    <p className="text-gray-600">{tournament.description}</p>
                  </div>
                )}

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}